import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeAPI } from "../../services/api";
import CheckoutModal from "./CheckoutModal";
import magazineStyles from "../../styles/magazine.css?raw";

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric"
});

const FALLBACK_COVER = "/assets/img/magazine/cover.jpg";

function formatIssueMonth(issue) {
  if (!issue?.month || !issue?.year) return "";
  return MONTH_FORMATTER.format(new Date(issue.year, issue.month - 1, 1));
}

function hasStoredToken() {
  return Boolean(localStorage.getItem("token"));
}

function pushRecentRead(issue) {
  if (!issue?._id) return;

  try {
    const current = JSON.parse(localStorage.getItem("magazine_recent_reads") || "[]");
    const next = [String(issue._id), ...current.filter((id) => String(id) !== String(issue._id))].slice(0, 12);
    localStorage.setItem("magazine_recent_reads", JSON.stringify(next));
  } catch {
    // no-op
  }
}

export default function MagazineLibrary() {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [libraryIssueIds, setLibraryIssueIds] = useState([]);
  const [ownedIssueIds, setOwnedIssueIds] = useState([]);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [libraryBusyId, setLibraryBusyId] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [planForModal, setPlanForModal] = useState(null);

  useEffect(() => {
    document.title = "My Library - CGES.CLUB";
  }, []);

  useEffect(() => {
    let active = true;

    async function fetchLibraryData() {
      if (!hasStoredToken()) {
        if (!active) return;
        setLoadError("Please log in to access your library.");
        setIsLoading(false);
        return;
      }

      try {
        const [dataResponse, accessResponse] = await Promise.all([
          makeAPI("/api/get_magazine_data", "POST", false, {}),
          makeAPI("/api/get_magazine_access", "POST", true, {})
        ]);

        if (!active) return;

        if (dataResponse?.status !== "S" || accessResponse?.status !== "S") {
          setLoadError("Unable to load your library right now.");
          return;
        }

        const currentIssue = dataResponse?.data?.current_issue ? [dataResponse.data.current_issue] : [];
        const pastIssues = Array.isArray(dataResponse?.data?.past_issues) ? dataResponse.data.past_issues : [];
        const allIssues = [...currentIssue, ...pastIssues];
        const nextLibraryIssueIds = Array.isArray(accessResponse?.data?.library_issue_ids) ? accessResponse.data.library_issue_ids : [];
        const nextOwnedIssueIds = Array.isArray(accessResponse?.data?.owned_issue_ids) ? accessResponse.data.owned_issue_ids : [];
        const visibleLibrary = allIssues.filter((issue, index, list) => {
          const issueId = String(issue?._id || "");
          const included = nextLibraryIssueIds.includes(issueId) || nextOwnedIssueIds.includes(issueId);
          const firstIndex = list.findIndex((item) => String(item?._id || "") === issueId);
          return included && firstIndex === index;
        });

        setIssues(visibleLibrary);
        setLibraryIssueIds(nextLibraryIssueIds);
        setOwnedIssueIds(nextOwnedIssueIds);
        setHasActiveSubscription(Boolean(accessResponse?.data?.has_active_subscription));
      } catch {
        if (!active) return;
        setLoadError("Unable to load your library right now.");
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    fetchLibraryData();
    return () => {
      active = false;
    };
  }, []);

  const issueInLibrary = (issue) => libraryIssueIds.includes(String(issue?._id || ""));
  const issueOwned = (issue) => ownedIssueIds.includes(String(issue?._id || ""));
  const issueCanRead = (issue) => Boolean(issue?.is_free || issueOwned(issue) || hasActiveSubscription);
  const issueCanBeSavedToLibrary = (issue) => Boolean(issue?.is_free || (hasActiveSubscription && !issueOwned(issue)));

  const libraryIssues = useMemo(
    () => issues.filter((issue) => issueInLibrary(issue) || issueOwned(issue)),
    [issues, libraryIssueIds, ownedIssueIds]
  );

  const openIssue = (issue) => {
    if (!issue?.pdf_url) return;

    pushRecentRead(issue);
    localStorage.setItem("selected_magazine_issue", JSON.stringify(issue));
    navigate(`/magazine/read/${issue.issue_number || "issue"}`, {
      state: { issue }
    });
  };

  const openIssueCheckout = (issue) => {
    if (!issue?._id) return;

    setPlanForModal({
      name: issue?.title || "Magazine Issue",
      amount: Number(issue?.price || 0),
      meta: "lifetime access for this issue",
      purchaseType: "issue",
      issueId: issue?._id,
      currencyType: issue?.currency_type || "INR"
    });
    setShowCheckout(true);
  };

  const handleLibraryToggle = async (issue) => {
    if (!issue?._id || !hasStoredToken()) return;
    if (!issueCanBeSavedToLibrary(issue)) return;

    try {
      setLibraryBusyId(String(issue._id));
      const response = await makeAPI("/api/toggle_magazine_library", "POST", true, {
        issue_id: issue._id
      });

      if (response?.status !== "S") {
        alert(response?.message || "Unable to update your library.");
        return;
      }

      setLibraryIssueIds(Array.isArray(response?.data?.library_issue_ids) ? response.data.library_issue_ids : []);
      setOwnedIssueIds(Array.isArray(response?.data?.owned_issue_ids) ? response.data.owned_issue_ids : []);
      setHasActiveSubscription(Boolean(response?.data?.has_active_subscription));
    } catch {
      alert("Unable to update your library.");
    } finally {
      setLibraryBusyId("");
    }
  };

  if (!hasStoredToken()) {
    return (
      <div className="container-fluid magazine-page">
        <section className="magazine-section">
          <div className="magazine-panel magazine-support-panel">
            <div className="magazine-kicker">My Library</div>
            <h3>Log in to access your saved issues</h3>
            <p>Your purchased issues and saved free issues will appear here after you log in.</p>
            <div className="magazine-support-panel__actions">
              <button
                type="button"
                className="btn magazine-btn magazine-btn--primary"
                onClick={() => navigate("/magazine")}
              >
                Back to Magazine
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <style>{magazineStyles}</style>
      <div className="container-fluid magazine-page">
        {showCheckout && (
          <CheckoutModal
            show={showCheckout}
            selectedPlan={planForModal || { name: "Single Issue", amount: 50, currencyType: "INR" }}
            onPurchaseSuccess={() => window.location.reload()}
            onClose={() => setShowCheckout(false)}
          />
        )}
        <section className="magazine-section">
          <div className="magazine-section__header">
            <div>
              <div className="magazine-kicker">Your Library</div>
              <h2>My Library</h2>
            </div>
            <div>
              <button
                type="button"
                className="btn magazine-btn magazine-btn--ghost"
                onClick={() => navigate("/magazine")}
              >
                <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                Back to Magazine
              </button>
            </div>
          </div>

          {isLoading ? <div className="magazine-panel p-4">Loading your library...</div> : null}
          {!isLoading && loadError ? <div className="magazine-panel p-4">{loadError}</div> : null}

          {!isLoading && !loadError ? (
            libraryIssues.length > 0 ? (
              <div className="magazine-archive-grid">
                {libraryIssues.map((issue) => (
                  <article key={`library-${issue?._id || issue?.issue_number || formatIssueMonth(issue)}`} className="magazine-carousel-card">
                    <div className="magazine-mini-cover">
                      <img
                        className="magazine-mini-cover__image"
                        src={issue.cover_image || FALLBACK_COVER}
                        alt={`${formatIssueMonth(issue)} cover`}
                      />
                      <div className="magazine-mini-cover__overlay">
                        {issueCanBeSavedToLibrary(issue) ? (
                          <div className="magazine-mini-cover__actions">
                            <button
                              type="button"
                              className="btn magazine-btn magazine-btn--ghost magazine-btn--icon"
                              onClick={() => handleLibraryToggle(issue)}
                              disabled={libraryBusyId === String(issue?._id)}
                              aria-label={issueInLibrary(issue) ? "Remove from library" : "Add to library"}
                              title={issueInLibrary(issue) ? "Remove from library" : "Add to library"}
                            >
                              <i className={`fa-solid ${issueInLibrary(issue) ? "fa-bookmark" : "fa-book-open-reader"}`} aria-hidden="true" />
                            </button>
                          </div>
                        ) : null}
                        <div className="magazine-mini-cover__brand">CGES.CLUB</div>
                        <div className="magazine-mini-cover__month">{formatIssueMonth(issue)}</div>
                        <div className="magazine-mini-cover__subtitle">{issue.description}</div>
                      </div>
                    </div>
                    <div className="magazine-carousel-card__body">
                      <div>
                        <div className="magazine-carousel-card__month">{formatIssueMonth(issue)}</div>
                        <div className="magazine-carousel-card__label">
                          {issueOwned(issue)
                            ? "Purchased"
                            : issue.is_free
                              ? "Saved Free Issue"
                              : "Saved From Subscription"}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn magazine-btn magazine-btn--primary"
                        onClick={() => (issueCanRead(issue) ? openIssue(issue) : openIssueCheckout(issue))}
                      >
                        {issueCanRead(issue) ? "Read" : "Buy"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="magazine-panel magazine-support-panel">
                <div className="magazine-kicker">Empty Library</div>
                <h3>No issues saved yet</h3>
                <p>Add free issues to your library or buy single issues to build your reading shelf here.</p>
                <div className="magazine-support-panel__actions">
                  <button
                    type="button"
                    className="btn magazine-btn magazine-btn--primary"
                    onClick={() => navigate("/magazine")}
                  >
                    Browse Magazine
                  </button>
                </div>
              </div>
            )
          ) : null}
        </section>
      </div>
    </>
  );
}
